import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

import {SharedModule} from '../../@commons/ui-components/shared.module';
import {SeedItemComponent} from '../@components/seed-item/seed-item.component';
import {CultivationApi} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-api';
import {SeedApi} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/seed/seed-api';
import {
  SeedListRequest
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/location/seed-list.request';
import {StorageService} from '../../@commons/@services/front/storage.service';
import {
  CultivationHierarchyApi
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-hierarchy/cultivation-hierarchy-api';
import {
  CalendarCultivationHierarchyRequest
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/cultivation/calendar-cultivation-hierarchy-list.request';
import {CultivationPeriodType} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/cultivation-period-type.enum';
import {SeedTiming, isTimingInMonth, seedTiming} from '../../@commons/@services/front/seed-timing.util';
import { isBulbOrPlant } from '../../@commons/@services/front/seed-agronomy.util';
import {
  SeedListResponse
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/seed-list.response';
import RoutesConstants from '../../@commons/constants/routes.constants';
import { ErrorStateComponent } from '../../@commons/ui-components/error-state/error-state.component';
import { SeedActionsToastComponent } from '../@toasts/seed-actions-toast/seed-actions-toast.component';
import { PlusIconComponent } from '../../@icons/plus-icon.component';
import { ScanBarcodeIconComponent } from '../../@icons/scan-barcode-icon.component';
import { SearchIconComponent } from '../../@icons/search-icon.component';
import { SeasonPlanBannerComponent } from '../../season-plan/season-plan-banner/season-plan-banner.component';
import { VwPluralPipe } from '../../@commons/pipes/vw-plural.pipe';
import { GrowingStage } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/growing-stage.enum';

/** Graines chargées par page : la liste se complète à la demande. */
const PAGE_SIZE = 20;

/**
 * Mes graines (maquette 09) : ce qui se sème ou se plante ce mois-ci en
 * tête, puis les graines, puis les bulbes et plants (K29). Le rangement suit
 * le même calcul que le calendrier (K17) : une culture qui ne se plante que
 * (ail, échalote) est un bulbe ou un plant.
 */
@Component({
    selector: 'vw-user-seeds', // Indicates that this component is standalone
    imports: [SharedModule, TranslatePipe, SeedItemComponent, ErrorStateComponent, SeedActionsToastComponent, PlusIconComponent, ScanBarcodeIconComponent, SearchIconComponent, SeasonPlanBannerComponent, VwPluralPipe], // Required modules for the component
    templateUrl: './user-seeds.component.html', // Path to the component's HTML template
    styleUrls: ['./user-seeds.component.scss'], // Corrected from styleUrl to styleUrls
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [{
            provide: CultivationApi,
            useClass: CultivationApi
        }, {
            provide: SeedApi,
            useClass: SeedApi
        }, {
            provide: CultivationHierarchyApi,
            useClass: CultivationHierarchyApi
        }]
})
export class UserSeedsComponent implements OnInit {
  seeds: SeedListResponse[] = []; // Array to hold seed data
  /** Pages 1-based côté API. */
  currentSeedPage = 1;
  /** Quand semer ou planter chaque culture chez soi, par uid de culture. */
  timings = new Map<string, SeedTiming>();
  /** « À planter ou semer maintenant » : la fenêtre couvre le mois en cours. */
  nowSeeds: SeedListResponse[] = [];
  /** Graines hors fenêtre du mois. */
  packetSeeds: SeedListResponse[] = [];
  /** « Bulbes et plants » : cultures qui ne se plantent que (K29). */
  bulbSeeds: SeedListResponse[] = [];
  /** Première page en cours : rien n'est encore dit, ni vide ni erreur. */
  isLoading = true;
  isLoadingMore = false;
  /** Échec du chargement : un écran d'erreur, jamais « aucune graine ». */
  failed = false;
  reachedEnd = false;
  showAddSheet = false;
  /** Mois de 1 à 12, lu une fois à l'ouverture. */
  private readonly month = new Date().getMonth() + 1;

  constructor(private router: Router,
              private storageService: StorageService,
              private seedApi: SeedApi,
              private cultivationHierarchyApi: CultivationHierarchyApi) {} // Injecting router and potager service

  /**
   * Lifecycle hook that is called after the component is initialized.
   * Fetches the list of seeds from the service.
   */
  ngOnInit() {
    this.loadSeeds();
    this.loadTimings();
  }

  /**
   * Les périodes de semis et de plantation des graines de l'utilisateur, au
   * climat de son adresse : une seule lecture du calendrier pour toute la
   * liste. En panne, les cartes restent sans mention, rien d'autre ne change.
   */
  async loadTimings() {
    const request = new CalendarCultivationHierarchyRequest();
    request.addressUid = this.storageService.getItem('address-uid') ?? undefined;
    request.mySeeds = true;
    request.cultivationPeriodTypes = [CultivationPeriodType.SEEDLING, CultivationPeriodType.PLANTING];
    try {
      const hierarchies = await this.cultivationHierarchyApi.calendar(request, this.storageService.getCurrentLanguage());
      const timings = new Map<string, SeedTiming>();
      for (const hierarchy of hierarchies ?? []) {
        for (const cultivation of hierarchy.cultivations ?? []) {
          const timing = seedTiming(cultivation.periods);
          if (timing) timings.set(cultivation.uid, timing);
        }
      }
      this.timings = timings;
      this.regroup();
    } catch (e) {
      console.warn('UserSeedsComponent: failed to load sowing periods', e);
    }
  }

  async loadSeeds() {
    try {
      const paging = await this.seedApi.list(new SeedListRequest(), this.currentSeedPage,
        PAGE_SIZE, this.storageService.getCurrentLanguage());
      const results = paging?.results ?? [];
      this.seeds.push(...results);
      // Une page incomplète, ou pas de page suivante, signe la fin.
      this.reachedEnd = results.length < PAGE_SIZE || paging?.page?.nextPage == null;
      this.regroup();
    } catch (e) {
      console.warn('UserSeedsComponent: failed to load seeds', e);
      if (this.currentSeedPage === 1) {
        this.failed = true;
      } else {
        // La page suivante sera redemandée au prochain « Voir plus ».
        this.currentSeedPage -= 1;
      }
    } finally {
      this.isLoading = false;
    }
  }

  /** Reste-t-il des graines à charger ? */
  get hasMore(): boolean {
    return !this.reachedEnd && !this.failed && this.seeds.length > 0;
  }

  async loadMore() {
    if (this.isLoadingMore || this.reachedEnd) return;
    this.isLoadingMore = true;
    this.currentSeedPage += 1;
    await this.loadSeeds();
    this.isLoadingMore = false;
  }

  /** Après un échec : on repart de la première page. */
  async retry() {
    this.seeds = [];
    this.currentSeedPage = 1;
    this.failed = false;
    this.reachedEnd = false;
    this.isLoading = true;
    this.regroup();
    await Promise.all([this.loadSeeds(), this.timings.size ? Promise.resolve() : this.loadTimings()]);
  }

  timingOf(seed: SeedListResponse): SeedTiming | null {
    return this.timings.get(seed.cultivation?.uid) ?? null;
  }

  /** Range les graines chargées dans les trois sections de l'écran. */
  private regroup() {
    const now: SeedListResponse[] = [];
    const packets: SeedListResponse[] = [];
    const bulbs: SeedListResponse[] = [];
    for (const seed of this.seeds) {
      const timing = this.timingOf(seed);
      // Un sachet vide n'a rien à semer : il reste à sa place, avec son stock.
      if (timing && seed.stockLevel > 0 && isTimingInMonth(timing, this.month)) {
        now.push(seed);
      } else if (isBulbOrPlant(seed.cultivation?.agronomy, timing)) {
        bulbs.push(seed);
      } else {
        packets.push(seed);
      }
    }
    this.nowSeeds = now;
    this.packetSeeds = packets;
    this.bulbSeeds = bulbs;
  }

  /**
   * Navigates to the seed details route when a seed is selected.
   * @param seed - The seed for which details are to be displayed
   */
  onSelectSeedDetails(seed: SeedListResponse) {
    this.router.navigate([`garden/seeds/${seed.uid}`], { state: { seedId: seed.uid } });
    // Navigate to the seed details route and pass the seed ID in the state
  }

  /**
   * « Planter » ou « Semer » : le parcours d'ajout de plante, culture déjà choisie
   * et ce geste présélectionné à « Où en est ta plante ? » (PC-07).
   */
  onPlantSeed(seed: SeedListResponse, event?: Event) {
    event?.stopPropagation();
    const kind = this.timingOf(seed)?.kind;
    this.router.navigate([RoutesConstants.GARDEN_ADDPLANT], {
      queryParams: {
        seedUid: seed.uid,
        cultivationUid: seed.cultivation?.uid,
        ...(kind ? { stage: kind === 'plant' ? GrowingStage.PLANT_OUT : GrowingStage.SOW } : {}),
      },
    });
  }

  onScan() {
    this.router.navigate([RoutesConstants.USER_ADD_SEED_SCAN]);
  }

  onSearchCatalog() {
    this.router.navigate([RoutesConstants.USER_ADD_SEED_SELECT]);
  }
}
